/**
 * Map category axis labels to picklist path_color from field metadata (report / summarizedData._metadata.group_fields).
 */

import {
  compareReportGroupBucketKeys,
  isDateGroupField,
} from '../matrix/matrixDateGrouping';
import { normalizePicklistCompareKey } from '../../../utils/picklistValueUtils';

export function coerceReportFieldsArray(reportFields) {
  if (Array.isArray(reportFields)) return reportFields;
  if (reportFields != null && typeof reportFields === 'object') {
    return Object.values(reportFields);
  }
  return [];
}

const DEFAULT_ALT_COLORS = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#4BC0C0',
  '#9966FF'
];

/**
 * @param {Array<object>|undefined} picklistValues — API/report often use value_key / value_label;
 *   layout & PathComponent use key / value / label (see object.model _picklistValuesJsonForLayoutField).
 * @returns {Map<string, string>} Map of each identifier string to path color
 */
export function normalizePicklistValuesArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function isPicklistGroupField(field) {
  const dt = String(field?.data_type || field?.field_type || '').toLowerCase();
  return dt === 'picklist' || dt === 'multipicklist';
}

/**
 * Picklist values for a group field (row metadata or full report field list).
 */
export function resolvePicklistValuesForGroupField(groupField, reportFields = []) {
  if (!groupField) return [];
  const fields = coerceReportFieldsArray(reportFields);
  const fromField =
    normalizePicklistValuesArray(groupField.picklist_values) ||
    normalizePicklistValuesArray(groupField.picklist_values_data) ||
    normalizePicklistValuesArray(groupField.picklistValues);
  if (fromField?.length) return fromField;

  const match = fields.find(
    (f) =>
      f.field_name === groupField.field_name &&
      (f.object_name || 'main') === (groupField.object_name || 'main')
  );
  if (!match) {
    return (
      normalizePicklistValuesArray(
        fields.find((f) => f.field_name === groupField.field_name)
          ?.picklist_values
      ) ||
      normalizePicklistValuesArray(
        fields.find((f) => f.field_name === groupField.field_name)
          ?.picklist_values_data
      ) ||
      []
    );
  }
  return (
    normalizePicklistValuesArray(match.picklist_values_data) ||
    normalizePicklistValuesArray(match.picklist_values) ||
    normalizePicklistValuesArray(match.picklistValues) ||
    []
  );
}

/**
 * Map picklist identifiers (key/label) → sort index (PicklistValueManager sort_order).
 */
export function buildPicklistSortIndex(picklistValues) {
  const indexByKey = new Map();
  if (!Array.isArray(picklistValues) || picklistValues.length === 0) {
    return indexByKey;
  }

  const ordered = [...picklistValues].sort((a, b) => {
    const oa = a.sort_order ?? a.sortOrder ?? 0;
    const ob = b.sort_order ?? b.sortOrder ?? 0;
    if (oa !== ob) return oa - ob;
    return String(a.value_label || a.label || a.value || '').localeCompare(
      String(b.value_label || b.label || b.value || ''),
      undefined,
      { sensitivity: 'base' }
    );
  });

  ordered.forEach((pv, idx) => {
    if (!pv) return;
    if (pv.value_key != null) indexByKey.set(normalizePicklistCompareKey(pv.value_key), idx);
    if (pv.value_label != null) indexByKey.set(normalizePicklistCompareKey(pv.value_label), idx);
    if (pv.key != null) indexByKey.set(normalizePicklistCompareKey(pv.key), idx);
    if (pv.value != null) indexByKey.set(normalizePicklistCompareKey(pv.value), idx);
    if (pv.label != null) indexByKey.set(normalizePicklistCompareKey(pv.label), idx);
  });

  return indexByKey;
}

function picklistSortRank(key, sortIndex, fallbackRank) {
  const s = normalizePicklistCompareKey(key);
  if (!s) return fallbackRank;
  if (sortIndex.has(s)) return sortIndex.get(s);
  for (const [k, rank] of sortIndex.entries()) {
    if (normalizePicklistCompareKey(k) === s) return rank;
  }
  return fallbackRank;
}

/**
 * Sort group row/column keys: picklist → configured sort_order; dates → chronological; else label.
 */
export function compareReportGroupKeys(
  a,
  b,
  groupField = null,
  reportFields = null,
  picklistValuesByFieldId = null
) {
  if (groupField && isPicklistGroupField(groupField)) {
    const picklistValues = getPicklistValuesForGroupField(
      groupField,
      reportFields,
      picklistValuesByFieldId
    );
    if (picklistValues.length > 0) {
      const sortIndex = buildPicklistSortIndex(picklistValues);
      const unknownRank = picklistValues.length;
      const ra = picklistSortRank(a, sortIndex, unknownRank);
      const rb = picklistSortRank(b, sortIndex, unknownRank);
      if (ra !== rb) return ra - rb;
      return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
  }

  if (groupField && isDateGroupField(groupField)) {
    return compareReportGroupBucketKeys(a, b);
  }

  return compareReportGroupBucketKeys(a, b);
}

export function sortReportGroupKeys(
  keys,
  groupField = null,
  reportFields = null,
  picklistValuesByFieldId = null
) {
  return [...(keys || [])].sort((a, b) =>
    compareReportGroupKeys(a, b, groupField, reportFields, picklistValuesByFieldId)
  );
}

export function getPicklistValuesForGroupField(
  groupField,
  reportFields = null,
  picklistValuesByFieldId = null
) {
  if (!groupField) return [];
  const fieldId = groupField.id || groupField.field_config_id || groupField.field_id;
  if (fieldId && picklistValuesByFieldId?.[String(fieldId)]?.length) {
    return picklistValuesByFieldId[String(fieldId)];
  }
  return resolvePicklistValuesForGroupField(groupField, reportFields);
}

function picklistEntryTokens(pv) {
  if (!pv) return [];
  return [
    pv.value_label,
    pv.value,
    pv.label,
    pv.value_key,
    pv.key,
  ]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v).trim().toLowerCase());
}

function dataKeyMatchesPicklistEntry(dataKey, keyMeta, tokens) {
  const candidates = new Set();
  const add = (v) => {
    if (v != null && String(v).trim() !== '') {
      candidates.add(String(v).trim().toLowerCase());
    }
  };
  add(dataKey);
  if (keyMeta) {
    add(keyMeta.label);
    (keyMeta.parts || []).forEach(add);
  }
  for (const t of tokens) {
    if (candidates.has(t)) return true;
  }
  return false;
}

/**
 * Order data keys using full picklist config (PicklistValueManager sort_order).
 * Unmatched keys are appended at the end.
 */
export function orderAxisKeysByPicklistValues(
  dataKeys,
  picklistValues,
  keyMeta = {}
) {
  const keyList = Array.from(dataKeys || []);
  if (!picklistValues?.length) return keyList;

  const sortedPicklist = [...picklistValues].sort((a, b) => {
    const oa = a.sort_order ?? a.sortOrder ?? 0;
    const ob = b.sort_order ?? b.sortOrder ?? 0;
    if (oa !== ob) return oa - ob;
    return String(a.value_label || a.label || '').localeCompare(
      String(b.value_label || b.label || ''),
      undefined,
      { sensitivity: 'base' }
    );
  });

  const ordered = [];
  const used = new Set();

  sortedPicklist.forEach((pv) => {
    const tokens = picklistEntryTokens(pv);
    for (const k of keyList) {
      if (used.has(k)) continue;
      if (dataKeyMatchesPicklistEntry(k, keyMeta[k], tokens)) {
        ordered.push(k);
        used.add(k);
        break;
      }
    }
  });

  keyList.forEach((k) => {
    if (!used.has(k)) ordered.push(k);
  });

  return ordered;
}

/** Sort matrix row/column axis keys (picklist sort_order, dates, labels). */
export function sortMatrixAxisKeys(
  keySet,
  groupFields,
  reportFields,
  keyMeta,
  picklistValuesByFieldId = null
) {
  const keys = Array.from(keySet || []);
  if (!groupFields?.length) {
    return keys.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
  }
  if (groupFields.length === 1) {
    const gf = groupFields[0];
    const pl = getPicklistValuesForGroupField(
      gf,
      reportFields,
      picklistValuesByFieldId
    );
    if (isPicklistGroupField(gf) && pl.length > 0) {
      return orderAxisKeysByPicklistValues(keys, pl, keyMeta);
    }
    return sortReportGroupKeys(keys, gf, reportFields);
  }
  return keys.sort((a, b) => {
    const metaA = keyMeta?.[a];
    const metaB = keyMeta?.[b];
    const partsA = metaA?.parts || String(a).split('|');
    const partsB = metaB?.parts || String(b).split('|');
    for (let i = 0; i < groupFields.length; i++) {
      const cmp = compareReportGroupKeys(
        partsA[i] ?? '',
        partsB[i] ?? '',
        groupFields[i],
        reportFields,
        picklistValuesByFieldId
      );
      if (cmp !== 0) return cmp;
    }
    return String(metaA?.label || a).localeCompare(String(metaB?.label || b), undefined, {
      numeric: true,
    });
  });
}

export function buildPicklistValueColorMap(picklistValues) {
  const map = new Map();
  if (!Array.isArray(picklistValues)) return map;
  for (const pv of picklistValues) {
    if (!pv) continue;
    const raw = pv.path_color != null && pv.path_color !== '' ? pv.path_color : pv.pathColor;
    if (raw == null || raw === '') continue;
    const col = String(raw).trim();
    // Report / reportbuilder field metadata
    if (pv.value_key != null) map.set(String(pv.value_key), col);
    if (pv.value_label != null) map.set(String(pv.value_label), col);
    // Layout path JSON: { key, value, path_color } (value = display label)
    if (pv.key != null) map.set(String(pv.key), col);
    if (pv.value != null) map.set(String(pv.value), col);
    if (pv.label != null) map.set(String(pv.label), col);
  }
  return map;
}

/**
 * @param {string} label - Group key as shown on chart (display value or value key)
 * @param {Map<string, string>} colorMap
 * @param {number} index - Fallback palette index
 * @param {string} [fieldFallback] - When no picklist color for label
 * @returns {string}
 */
export function colorForLabelFromPicklistMap(label, colorMap, index, fieldFallback) {
  if (!colorMap || colorMap.size === 0) return fieldFallback;
  const s = String(label);
  if (colorMap.has(s)) return colorMap.get(s);
  for (const [k, v] of colorMap.entries()) {
    if (String(k).trim() === s.trim()) return v;
  }
  return fieldFallback != null ? fieldFallback : DEFAULT_ALT_COLORS[index % DEFAULT_ALT_COLORS.length];
}

/**
 * One color per category (label) for bar/line point/pie slice when the group field is picklist.
 * @param {string[]} labels
 * @param {object|undefined} groupFieldInfo - metadata.group_fields[fieldName]
 * @param {string|undefined} valueFieldColor - default field color when not picklist or unmapped
 * @param {number} [valueFieldIndex] - for DEFAULT_ALT_COLORS
 * @returns {string[]|null} - null = use single field color, not per-category
 */
export function getCategoryAxisColorsForChart(labels, groupFieldInfo, valueFieldColor, valueFieldIndex = 0) {
  if (!groupFieldInfo || !Array.isArray(labels) || labels.length === 0) return null;
  // Prefer any picklist row with path_color; do not require data_type (path fields still picklist in DB;
  // some payloads omit or vary data_type on group metadata).
  const map = buildPicklistValueColorMap(groupFieldInfo.picklist_values);
  if (map.size === 0) return null;
  const fieldFallback = valueFieldColor || DEFAULT_ALT_COLORS[valueFieldIndex % DEFAULT_ALT_COLORS.length];
  return labels.map((lab, i) => colorForLabelFromPicklistMap(lab, map, i, fieldFallback));
}

/**
 * @param {string} hexOrCss
 * @param {string} alphaHex - e.g. '40' for ChartDisplay area fill hack
 * @returns {string}
 */
export function appendAlphaIfHex(hexOrCss, alphaHex = '40') {
  if (typeof hexOrCss !== 'string') return hexOrCss;
  const t = hexOrCss.trim();
  if (t.startsWith('#') && t.length === 7) return `${t}${alphaHex}`;
  return t;
}

export { DEFAULT_ALT_COLORS };
